import React from 'react'
import ProtectedRoute from './components/ProtectedRoute'
import ModuleRoute from './components/ModuleRoute'
import ModuleDisabled from './views/errors/ModuleDisabled'

const Dashboard = React.lazy(() => import('./views/dashboard/Dashboard'))
const Profile = React.lazy(() => import('./views/profile/Profile'))
const Members = React.lazy(() => import('./views/members/Members'))
const MapPage = React.lazy(() => import('./views/map/Map'))
const EventsIndex = React.lazy(() => import('./views/calendar/EventsIndex'))
const EventDetailPage = React.lazy(() => import('./views/calendar/EventForm/EventDetailPage'))
const EventFormPage = React.lazy(() => import('./views/calendar/EventForm/EventFormPage'))
const MyTicketPage = React.lazy(() => import('./views/calendar/EventForm/MyTicketPage'))
const EventRegistrationsAdmin = React.lazy(() => import('./views/calendar/EventForm/EventRegistrationsAdmin'))
const Login = React.lazy(() => import('./views/pages/login/Login'))
const VerifyProfile = React.lazy(() => import('./views/VerifyProfile'))
const Posts = React.lazy(() => import('./views/pages/posts/Posts'))
const PostDetail = React.lazy(() => import('./views/pages/posts/PostDetail'))
const JobListing = React.lazy(() => import('./views/jobs/JobListing'))
const JobCreate = React.lazy(() => import('./views/jobs/JobCreate'))
const JobDetail = React.lazy(() => import('./views/jobs/JobDetail'))
const JobEdit = React.lazy(() => import('./views/jobs/JobEdit'))
const MyApplications = React.lazy(() => import('./views/jobs/MyApplications'))
const AccommodationsList = React.lazy(() => import('./views/accommodations/AccommodationsList'))
const AccommodationDetail = React.lazy(() => import('./views/accommodations/AccommodationDetail'))
const AccommodationCreate = React.lazy(() => import('./views/accommodations/AccommodationCreate'))
const AccommodationEdit = React.lazy(() => import('./views/accommodations/AccommodationEdit'))
const AdminAccommodationsPanel = React.lazy(
  () => import('./views/accommodations/AdminAccommodationsPanel'),
)
const AccommodationsMap = React.lazy(() => import('./views/accommodations/AccommodationsMap'))
const ChangePassword = React.lazy(() => import('./views/pages/changePassword/ChangePassword'))
const AccommodationOwnerDashboard = React.lazy(
  () => import('./views/accommodations/components/AccommodationOwnerDashboard'),
)
const AccommodationHome = React.lazy(() => import('./views/accommodations/AccommodationHome'))
const Discussions = React.lazy(() => import('./views/Discussions/Discussions'))
const DiscussionDetail = React.lazy(() => import('./views/Discussions/DiscussionDetail'))
const DiscussionsWrapper = React.lazy(() => import('./views/Discussions/DiscussionsWrapper'))
const BillingPage = React.lazy(() => import('./views/settings/BillingPage'))
const CalendarSettingsPage = React.lazy(() => import('./views/settings/CalendarSettingsPage'))
const SecuritySettingsPage = React.lazy(() => import('./views/settings/SecuritySettingsPage'))
const PayContribution = React.lazy(() => import('./views/contributions/PayContribution'))
const PaymentSuccess = React.lazy(() => import('./views/contributions/PaymentSuccess'))
const MyContribution = React.lazy(() => import('./views/contributions/MyContribution'))
const VerifyCertificate = React.lazy(() => import('./views/contributions/VerifyCertificate'))
const AdminOnboarding = React.lazy(() => import('./views/onboarding/AdminOnboarding'))
const Help = React.lazy(() => import('./views/help/Help'))
const SubscriptionRequired = React.lazy(
  () => import('./components/subscription/SubscriptionRequired'),
)

const SubscriptionSuccess = React.lazy(() => import('./views/subscription/SubscriptionSuccess'))

import { AccommodationProvider } from './contexts/AccommodationContext'
import Page404 from './views/pages/page404/Page404'
const SubscriptionExpiredAdmin = React.lazy(
  () => import('./views/subscription/SubscriptionExpiredAdmin'),
)
const SubscriptionExpiredMember = React.lazy(
  () => import('./views/subscription/SubscriptionExpiredMember'),
)

// Super-Admin Dashboard
import AdminProtectedRoute from './components/AdminProtectedRoute'
import AdminLayout from './layout/AdminLayout'
const AdminLogin = React.lazy(() => import('./views/admin/AdminLogin'))
const AdminDashboard = React.lazy(() => import('./views/admin/AdminDashboard'))
const AdminOrganizations = React.lazy(() => import('./views/admin/AdminOrganizations'))
const AdminWebhooks = React.lazy(() => import('./views/admin/AdminWebhooks'))
const AdminSystem = React.lazy(() => import('./views/admin/AdminSystem'))
const AdminDeployJobs = React.lazy(() => import('./views/admin/AdminDeployJobs'))
const AdminJobDetails = React.lazy(() => import('./views/admin/AdminJobDetails'))

// Network Intel
const NetworkIntel = React.lazy(() => import('./views/networkIntel/NetworkIntel'))
const BusinessReferralsPage = React.lazy(
  () => import('./views/businessReferrals/BusinessReferralsPage'),
)
const ClubMeetingsList = React.lazy(() => import('./views/clubMeetings/MeetingsList'))
const ClubMeetingDetail = React.lazy(() => import('./views/clubMeetings/MeetingDetail'))
const MentoringPage = React.lazy(() => import('./views/mentoring/MentoringPage'))
const RcpSurveysPage = React.lazy(() => import('./views/rcpSurveys/RcpSurveysPage'))
const CampaignsListPage = React.lazy(() => import('./views/campaigns/CampaignsListPage'))
// PublicInsertionResults et PublicSurveyResponseForm sont déclarés directement
// dans App.js (routes publiques sans gate auth)
const PromotionsAdmin = React.lazy(() => import('./views/admin/PromotionsAdmin'))
// MentorApplicationsAdmin n'est plus une route standalone depuis Sprint
// Vidal M1.5 commit 2e — il est utilisé en mode `embedded` dans
// MentoringManagementTab (drill-down /dashboard?tab=mentoring).
// L'ancienne route /admin/mentor-applications redirige proprement vers
// le cockpit unifié pour préserver les bookmarks legacy.
const MentorApplicationsRedirect = React.lazy(() => import('./views/admin/MentorApplicationsRedirect'))

// ✅ WEEK 4: Dashboard fondateur
const FounderDashboard = React.lazy(() => import('./views/internal/FounderDashboard'))

// Créer un composant wrapper pour la vérification de profil
const VerifyProfileWrapper = () => {
  const handleVerificationSuccess = () => {
    // Attendre 2 secondes avant la redirection pour voir les confetti
    setTimeout(() => {
      window.location.href = '/publications'
    }, 2000) // 2000ms = 2 secondes
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <VerifyProfile onVerificationSuccess={handleVerificationSuccess} />
    </div>
  )
}

const routes = [
  { path: '/login', name: 'Connexion', element: <Login /> },
  {
    path: '/404',
    name: 'Page non trouvée',
    element: <Page404 />,
  },
  {
    path: '/verify-profile',
    name: 'Vérification du profil',
    element: (
      <ProtectedRoute noLayout={true}>
        <VerifyProfileWrapper />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin-onboarding',
    name: 'Configuration plateforme',
    element: (
      <ProtectedRoute noLayout={true}>
        <AdminOnboarding />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/profile',
    name: 'Profil',
    element: (
      <ProtectedRoute>
        <Profile />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/accommodations',
    name: 'Admin Hébergement',
    element: (
      <ProtectedRoute>
        <AdminAccommodationsPanel />
      </ProtectedRoute>
    ),
  },
  {
    path: '/accommodations/dashboard',
    name: 'Admin Hébergement',
    element: (
      <ProtectedRoute>
        <AccommodationOwnerDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/change-password',
    name: 'Changement de mot de passe',
    element: <ChangePassword />,
  },
  {
    path: '/subscription/success',
    name: 'Abonnement activé',
    element: (
      <ProtectedRoute>
        <SubscriptionSuccess />
      </ProtectedRoute>
    ),
  },
  {
    path: '/subscription-required',
    name: 'Abonnement requis',
    element: (
      <ProtectedRoute>
        <SubscriptionRequired />
      </ProtectedRoute>
    ),
  },
  {
    path: '/subscription/expired',
    name: 'Abonnement expiré (Admin)',
    element: (
      <ProtectedRoute>
        <SubscriptionExpiredAdmin />
      </ProtectedRoute>
    ),
  },
  {
    path: '/subscription/required',
    name: 'Abonnement requis (Membre)',
    element: (
      <ProtectedRoute>
        <SubscriptionExpiredMember />
      </ProtectedRoute>
    ),
  },
  {
    path: '/contributions/pay',
    name: 'Payer ma cotisation',
    element: (
      <ProtectedRoute>
        <PayContribution />
      </ProtectedRoute>
    ),
  },
  {
    path: '/contributions/success',
    name: 'Paiement réussi',
    element: (
      <ProtectedRoute>
        <PaymentSuccess />
      </ProtectedRoute>
    ),
  },
  {
    path: '/my-contribution',
    name: 'Ma cotisation',
    element: (
      <ProtectedRoute>
        <MyContribution />
      </ProtectedRoute>
    ),
  },
  {
    path: '/verify/:uuid',
    name: 'Vérification certificat',
    element: <VerifyCertificate />, // Route publique, pas de ProtectedRoute
  },
  {
    path: '/members',
    name: 'Membres',
    element: (
      <ModuleRoute moduleName="members">
        <Members />
      </ModuleRoute>
    ),
  },
  {
    path: '/map',
    name: 'Carte',
    element: (
      <ModuleRoute moduleName="map">
        <MapPage />
      </ModuleRoute>
    ),
  },
  // ===== NOUVELLES ROUTES EVENTS =====
  {
    path: '/events',
    name: 'Événements',
    element: (
      <ModuleRoute moduleName="events">
        <EventsIndex />
      </ModuleRoute>
    ),
  },
  {
    path: '/events/calendar',
    name: 'Calendrier des événements',
    element: (
      <ModuleRoute moduleName="events">
        <EventsIndex />
      </ModuleRoute>
    ),
  },
  {
    path: '/events/cards',
    name: 'Événements - Liste',
    element: <EventsIndex />,
  },
  {
    path: '/events/create',
    name: 'Créer un événement',
    element: (
      <ModuleRoute moduleName="events">
        <EventFormPage />
      </ModuleRoute>
    ),
  },
  {
    path: '/events/:id/edit',
    name: 'Modifier un événement',
    element: (
      <ModuleRoute moduleName="events">
        <EventFormPage />
      </ModuleRoute>
    ),
  },
  {
    path: '/events/:id',
    name: "Détails de l'événement",
    element: (
      <ModuleRoute moduleName="events">
        <EventDetailPage />
      </ModuleRoute>
    ),
  },
  {
    path: '/events/:eventId/my-ticket',
    name: 'Mon ticket',
    element: (
      <ModuleRoute moduleName="events">
        <MyTicketPage />
      </ModuleRoute>
    ),
  },
  {
    path: '/events/:eventId/registrations',
    name: 'Gestion des inscriptions',
    element: (
      <ModuleRoute moduleName="events">
        <EventRegistrationsAdmin />
      </ModuleRoute>
    ),
  },
  // ========================================
  {
    path: '/jobs',
    name: "Offres d'emploi",
    element: (
      <ModuleRoute moduleName="jobs">
        <JobListing />
      </ModuleRoute>
    ),
  },
  {
    path: '/jobs/create',
    name: 'Créer une offre',
    element: (
      <ModuleRoute moduleName="jobs">
        <JobCreate />
      </ModuleRoute>
    ),
  },
  {
    path: '/jobs/my-applications',
    name: 'Mes candidatures',
    element: (
      <ModuleRoute moduleName="jobs">
        <MyApplications />
      </ModuleRoute>
    ),
  },
  {
    path: '/jobs/:id',
    name: "Détail de l'offre",
    element: (
      <ModuleRoute moduleName="jobs">
        <JobDetail />
      </ModuleRoute>
    ),
  },
  {
    path: '/jobs/:id/edit',
    name: "Modifier l'offre",
    element: (
      <ModuleRoute moduleName="jobs">
        <JobEdit />
      </ModuleRoute>
    ),
  },
  {
    path: '/publications',
    name: 'Publications',
    element: (
      <ModuleRoute moduleName="posts">
        <Posts />
      </ModuleRoute>
    ),
  },
  {
    path: '/publications/:id',
    name: 'Détail publication',
    element: (
      <ModuleRoute moduleName="posts">
        <PostDetail />
      </ModuleRoute>
    ),
  },
  {
    path: '/members/:id',
    name: 'Détail membre',
    element: (
      <ModuleRoute moduleName="members">
        <Members />
      </ModuleRoute>
    ),
  },
  {
    path: '/module-disabled',
    name: 'Module Désactivé',
    element: <ModuleDisabled />,
  },
  {
    path: '/accommodations',
    name: 'Hébergements',
    element: (
      <ModuleRoute moduleName="accommodations">
        <AccommodationProvider>
          <AccommodationsList />
        </AccommodationProvider>
      </ModuleRoute>
    ),
  },
  {
    path: '/accommodations/map',
    name: 'Carte des hébergements',
    element: (
      <ModuleRoute moduleName="accommodations">
        <AccommodationProvider>
          <AccommodationsMap />
        </AccommodationProvider>
      </ModuleRoute>
    ),
  },
  {
    path: '/accommodations/create',
    name: 'Créer un hébergement',
    element: (
      <ModuleRoute moduleName="accommodations">
        <AccommodationProvider>
          <AccommodationCreate />
        </AccommodationProvider>
      </ModuleRoute>
    ),
  },
  {
    path: '/accommodations/home',
    name: 'Hébergements - Accueil',
    element: (
      <ModuleRoute moduleName="accommodations">
        <AccommodationProvider>
          <AccommodationHome />
        </AccommodationProvider>
      </ModuleRoute>
    ),
  },
  {
    path: '/accommodations/:id',
    name: "Détail de l'hébergement",
    element: (
      <ModuleRoute moduleName="accommodations">
        <AccommodationProvider>
          <AccommodationDetail />
        </AccommodationProvider>
      </ModuleRoute>
    ),
  },
  {
    path: '/accommodations/:id/edit',
    name: "Modifier l'hébergement",
    element: (
      <ModuleRoute moduleName="accommodations">
        <AccommodationProvider>
          <AccommodationEdit />
        </AccommodationProvider>
      </ModuleRoute>
    ),
  },
  {
    path: '/discussions',
    name: 'Discussions',
    element: (
      <ModuleRoute moduleName="discussions">
        <Discussions />
      </ModuleRoute>
    ),
  },
  {
    path: '/discussions/:groupId',
    name: 'Détail Groupe',
    element: (
      <ModuleRoute moduleName="discussions">
        <DiscussionDetail />
      </ModuleRoute>
    ),
  },
  // ===== TROUVER UN CONTACT (network_radar) =====
  // name = clé technique neutre (même convention que business_referrals) : le
  // nom affiché vit dans nav.json, il ne doit pas être dupliqué en dur ici.
  {
    path: '/network-intel',
    name: 'network_radar',
    element: (
      <ModuleRoute moduleName="network_radar">
        <NetworkIntel />
      </ModuleRoute>
    ),
  },
  // ===== APPORT D'AFFAIRES (business_referrals) =====
  // name = clé technique neutre (pas de chaîne FR en dur) ; le breadcrumb repo
  // n'est pas i18n (dette existante). i18n des noms de route = chantier séparé.
  {
    path: '/business-referrals',
    name: 'business_referrals',
    element: (
      <ModuleRoute moduleName="business_referrals">
        <BusinessReferralsPage />
      </ModuleRoute>
    ),
  },
  {
    path: '/club-meetings',
    name: 'club_meetings',
    element: (
      <ModuleRoute moduleName="club_meetings">
        <ClubMeetingsList />
      </ModuleRoute>
    ),
  },
  {
    path: '/club-meetings/:eventId',
    name: 'club_meeting_detail',
    element: (
      <ModuleRoute moduleName="club_meetings">
        <ClubMeetingDetail />
      </ModuleRoute>
    ),
  },
  // ===== MENTORAT =====
  {
    path: '/mentoring',
    name: 'Mentorat',
    element: (
      <ModuleRoute moduleName="mentoring">
        <MentoringPage />
      </ModuleRoute>
    ),
  },
  // ===== ENQUÊTES D'INSERTION (RCP) =====
  {
    path: '/rcp-surveys',
    name: 'Enquêtes d\'insertion',
    element: (
      <ModuleRoute moduleName="rcp_surveys">
        <RcpSurveysPage />
      </ModuleRoute>
    ),
  },
  // Routes publiques RCP (insertion-results, /rcp/r/:token) déclarées dans App.js
  // ===== CAMPAGNES (Lot 9b fundraising) — découverte, TOUT membre =====
  // /campaigns/:slug (page publique donateur, sans shell admin) est déclarée
  // dans App.js, hors de cet arbre — pas de collision : profondeurs de
  // chemin différentes.
  {
    path: '/campaigns',
    name: 'Campagnes',
    element: (
      <ModuleRoute moduleName="donations">
        <CampaignsListPage />
      </ModuleRoute>
    ),
  },
  // Lot 7/10 : le registre des dons (DonationsPage.js) n'a plus de route
  // standalone /donations — il vit désormais dans l'onglet "Fundraising" du
  // dashboard de pilotage (src/views/dashboard/tabs/FundraisingTab.js).
  // ===== PROMOTIONS (admin) =====
  {
    path: '/admin/promotions',
    name: 'Promotions',
    element: (
      <ProtectedRoute>
        <PromotionsAdmin />
      </ProtectedRoute>
    ),
  },
  // ===== REDIRECT legacy (Sprint Vidal M1.5 commit 2e) =====
  // L'ancienne route standalone est désormais redirigée vers le drill-down
  // unifié /dashboard?tab=mentoring (MentoringManagementTab). Préserve
  // les bookmarks legacy + les liens emails internes qui pointeraient
  // sur l'ancienne URL.
  {
    path: '/admin/mentor-applications',
    name: 'Candidatures mentor (redirect)',
    element: (
      <ProtectedRoute>
        <MentorApplicationsRedirect />
      </ProtectedRoute>
    ),
  },
  // Note : AlumniSignupAdmin n'a pas de route standalone — il est intégré
  // dans le dashboard cockpit, sous-tab 'École & Filières' (alumni only).
  // Cf. views/dashboard/tabs/ModuleManagementTab.js.
  // Note : la validation des recruteurs B2B est intégrée dans le Dashboard
  // cockpit admin (tab `recruiterValidation`), pas en route standalone.
  // Cf. views/dashboard/tabs/RecruiterValidationTab.js.
  // ===== AIDE =====
  {
    path: '/help',
    name: 'Aide',
    element: (
      <ProtectedRoute>
        <Help />
      </ProtectedRoute>
    ),
  },
  // ===== DASHBOARD FONDATEUR (WEEK 4) =====
  {
    path: '/internal/dashboard',
    name: 'Dashboard Fondateur',
    element: (
      <ProtectedRoute>
        <FounderDashboard />
      </ProtectedRoute>
    ),
  },
  // ===== SETTINGS =====
  {
    path: '/settings/billing',
    name: 'Abonnement & Facturation',
    element: (
      <ProtectedRoute>
        <BillingPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/calendar',
    name: 'Calendrier',
    element: (
      <ProtectedRoute>
        <CalendarSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings/security',
    name: 'Sécurité',
    element: (
      <ProtectedRoute>
        <SecuritySettingsPage />
      </ProtectedRoute>
    ),
  },
  // ===== SUPER-ADMIN DASHBOARD =====
  {
    path: '/admin/login',
    name: 'Admin Login',
    element: <AdminLogin />,
  },
  {
    path: '/admin',
    name: 'Admin Dashboard',
    element: (
      <AdminProtectedRoute>
        <AdminLayout>
          <AdminDashboard />
        </AdminLayout>
      </AdminProtectedRoute>
    ),
  },
  {
    path: '/admin/organizations',
    name: 'Admin - Organisations',
    element: (
      <AdminProtectedRoute>
        <AdminLayout>
          <AdminOrganizations />
        </AdminLayout>
      </AdminProtectedRoute>
    ),
  },
  {
    path: '/admin/webhooks',
    name: 'Admin - Webhooks',
    element: (
      <AdminProtectedRoute>
        <AdminLayout>
          <AdminWebhooks />
        </AdminLayout>
      </AdminProtectedRoute>
    ),
  },
  {
    path: '/admin/system',
    name: 'Admin - Système',
    element: (
      <AdminProtectedRoute>
        <AdminLayout>
          <AdminSystem />
        </AdminLayout>
      </AdminProtectedRoute>
    ),
  },
  {
    path: '/admin/deploy-jobs',
    name: 'Admin - Jobs de Déploiement',
    element: (
      <AdminProtectedRoute>
        <AdminLayout>
          <AdminDeployJobs />
        </AdminLayout>
      </AdminProtectedRoute>
    ),
  },
  {
    path: '/admin/deploy-jobs/:id',
    name: 'Admin - Détails Job',
    element: (
      <AdminProtectedRoute>
        <AdminLayout>
          <AdminJobDetails />
        </AdminLayout>
      </AdminProtectedRoute>
    ),
  },
]

export default routes
